/**
 * WhatsApp Connect Component
 * Form cấu hình WhatsApp → QR scan / pairing code → Polling status → Success/Error
 */
import React, { useState, useEffect, useRef } from 'react';
import { fetchData } from '../../../lib/apis';
import { notification, Button, Spin, Card, Steps, Alert, Result, Input, Radio } from 'antd';
import { QrcodeOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, MessageOutlined, LinkOutlined, MobileOutlined } from '@ant-design/icons';

const { Step } = Steps;
const WHATSAPP_PROVIDER = 'baileys';

const WhatsAppConnect = ({ onSuccess, onCancel, companyId }) => {
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [channelId, setChannelId] = useState(null);
    const [qrData, setQrData] = useState(null);
    const [pairingCode, setPairingCode] = useState('');
    const [channelData, setChannelData] = useState(null);
    const [authMode, setAuthMode] = useState('qr');
    const [phoneNumber, setPhoneNumber] = useState('');
    const pollingRef = useRef(null);

    const onFinishConfig = async () => {
        setLoading(true);
        setError(null);
        setQrData(null);
        setPairingCode('');
        try {
            const response = await fetchData({
                url: 'api/v1/whatsapp/connect',
                method: 'POST',
                data: {
                    company_id: companyId,
                    provider: WHATSAPP_PROVIDER,
                    auth_mode: authMode,
                    phone_number: authMode === 'pairing_code' ? phoneNumber : undefined
                }
            });

            if (response.code !== 200) {
                throw new Error(response.message || 'Không thể khởi tạo session');
            }

            setChannelId(response.data.channel_id);
            setChannelData(response.data);
            setPairingCode(response.data?.pairing_code || '');

            if (response.data?.status === 'WORKING' || response.data?.already_connected) {
                setStep(2);
                notification.success({
                    message: 'Thành công',
                    description: 'WhatsApp đã được kết nối sẵn.'
                });
                if (onSuccess) onSuccess(response.data);
                return;
            }

            setStep(1);
            if (authMode === 'qr') {
                fetchQR(response.data.channel_id);
            }
            startPolling(response.data.channel_id);
        } catch (err) {
            setError(err.message || 'Lỗi kết nối server');
            setStep(3);
        } finally {
            setLoading(false);
        }
    };

    const fetchQR = async (chId) => {
        try {
            const response = await fetchData({
                url: `api/v1/whatsapp/qr?channel_id=${chId}`
            });
            if (response.code === 200 && response.data) {
                setQrData(response.data);
            }
        } catch (err) {
            console.error('Fetch QR error:', err);
        }
    };

    const startPolling = (chId) => {
        if (pollingRef.current) clearInterval(pollingRef.current);

        pollingRef.current = setInterval(async () => {
            try {
                const response = await fetchData({
                    url: `api/v1/whatsapp/status?channel_id=${chId}`
                });

                const status = response.data?.status;
                if (response.data?.pairing_code) {
                    setPairingCode(response.data.pairing_code);
                }
                setChannelData((prev) => ({ ...(prev || {}), ...(response.data || {}) }));

                if (status === 'WORKING') {
                    clearInterval(pollingRef.current);
                    setStep(2);
                    notification.success({
                        message: 'Thành công',
                        description: 'Đã kết nối WhatsApp thành công!'
                    });
                    if (onSuccess) onSuccess(response.data);
                } else if (status === 'FAILED' || status === 'STOPPED' || status === 'DISCONNECTED') {
                    clearInterval(pollingRef.current);
                    setError(response.data?.last_error || 'Session bị dừng hoặc lỗi. Vui lòng thử lại.');
                    setStep(3);
                } else if (status === 'SCAN_QR_CODE' && authMode === 'qr') {
                    fetchQR(chId);
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 3000);
    };

    useEffect(() => {
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    const resetToStart = () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        setStep(0);
        setError(null);
        setQrData(null);
        setPairingCode('');
        setChannelId(null);
        setChannelData(null);
    };

    const renderLinkingStep = () => {
        if (authMode === 'pairing_code') {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Alert
                        message="Nhập mã liên kết"
                        description="Mở WhatsApp trên điện thoại → Thiết bị được liên kết → Liên kết bằng số điện thoại → nhập mã bên dưới"
                        type="info"
                        showIcon
                        style={{ marginBottom: 20 }}
                    />

                    <div style={{
                        border: '2px solid #25D366',
                        borderRadius: 12,
                        padding: 24,
                        display: 'inline-block',
                        background: '#fff',
                        minWidth: 280
                    }}>
                        <div style={{ fontSize: 12, color: '#777', marginBottom: 12 }}>Mã liên kết</div>
                        <div style={{ fontSize: 36, fontWeight: 'bold', letterSpacing: 8, color: '#25D366' }}>
                            {pairingCode || '......'}
                        </div>
                        <div style={{ marginTop: 12, color: '#666' }}>
                            {phoneNumber ? `Số điện thoại: ${phoneNumber}` : 'Đang tạo mã liên kết...'}
                        </div>
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <SyncOutlined spin style={{ marginRight: 8, color: '#25D366' }} />
                        <span>Đang chờ xác nhận trên điện thoại...</span>
                    </div>
                </div>
            );
        }

        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <Alert
                    message="Quét mã QR"
                    description="Mở WhatsApp trên điện thoại → Cài đặt → Thiết bị được liên kết → Liên kết thiết bị → Quét mã QR bên dưới"
                    type="info"
                    showIcon
                    style={{ marginBottom: 20 }}
                />

                {qrData ? (
                    <div style={{
                        border: '2px solid #25D366',
                        borderRadius: 12,
                        padding: 16,
                        display: 'inline-block',
                        background: '#fff'
                    }}>
                        {typeof qrData === 'string' && qrData.startsWith('data:') ? (
                            <img src={qrData} alt="WhatsApp QR Code" style={{ width: 256, height: 256 }} />
                        ) : qrData?.value ? (
                            <div style={{ padding: 20 }}>
                                <p style={{ wordBreak: 'break-all', fontSize: 12, color: '#666' }}>{qrData.value}</p>
                            </div>
                        ) : (
                            <Spin size="large" />
                        )}
                    </div>
                ) : (
                    <div style={{ padding: 40 }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>Đang tạo mã QR...</p>
                    </div>
                )}

                <div style={{ marginTop: 20 }}>
                    <SyncOutlined spin style={{ marginRight: 8, color: '#25D366' }} />
                    <span>Đang chờ quét mã...</span>
                </div>

                <Button type="link" onClick={() => fetchQR(channelId)} style={{ marginTop: 16 }}>
                    Tạo mã QR mới
                </Button>
            </div>
        );
    };

    const renderStepContent = () => {
        switch (step) {
            case 0:
                return (
                    <div>
                        <Alert
                            message="Hướng dẫn kết nối WhatsApp"
                            description={
                                <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                                    <li>Mỗi lần kết nối sẽ tạo thêm một session WhatsApp mới cho công ty</li>
                                    <li>Chọn cách liên kết phù hợp: quét QR hoặc pairing code</li>
                                    <li>QR phù hợp khi có điện thoại sẵn để quét trực tiếp</li>
                                    <li>Pairing code phù hợp khi muốn liên kết bằng số điện thoại</li>
                                </ul>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 20 }}
                        />

                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Cách liên kết</div>
                            <Radio.Group value={authMode} onChange={(e) => setAuthMode(e.target.value)}>
                                <Radio.Button value="qr">QR Code</Radio.Button>
                                <Radio.Button value="pairing_code">Pairing Code</Radio.Button>
                            </Radio.Group>
                        </div>

                        {authMode === 'pairing_code' && (
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Số điện thoại WhatsApp</div>
                                <Input
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    placeholder="Ví dụ: 0912345678"
                                    prefix={<MobileOutlined />}
                                />
                            </div>
                        )}

                        <div style={{ textAlign: 'right', marginTop: 16 }}>
                            <Button style={{ marginRight: 8 }} onClick={onCancel}>Hủy</Button>
                            <Button
                                type="primary"
                                loading={loading}
                                style={{ background: '#25D366', borderColor: '#25D366' }}
                                icon={<MessageOutlined />}
                                onClick={() => onFinishConfig()}
                            >
                                Bắt đầu kết nối
                            </Button>
                        </div>
                    </div>
                );

            case 1:
                return renderLinkingStep();

            case 2:
                return (
                    <Result
                        status="success"
                        title="Kết nối thành công!"
                        subTitle={`WhatsApp đã sẵn sàng nhận tin nhắn${channelData?.session_name ? ` (Session: ${channelData.session_name})` : ''}`}
                        extra={[
                            <Button type="primary" key="close" onClick={onCancel} style={{ background: '#25D366', borderColor: '#25D366' }}>
                                Hoàn tất
                            </Button>
                        ]}
                    />
                );

            case 3:
                return (
                    <Result
                        status="error"
                        title="Kết nối thất bại"
                        subTitle={error || 'Đã có lỗi xảy ra'}
                        extra={[
                            <Button type="primary" key="retry" onClick={resetToStart} style={{ background: '#25D366', borderColor: '#25D366' }}>
                                Thử lại
                            </Button>,
                            <Button key="cancel" onClick={onCancel}>Hủy</Button>
                        ]}
                    />
                );

            default:
                return null;
        }
    };

    const currentStep = step === 3 ? 2 : step;

    return (
        <Card
            title={
                <span>
                    <MessageOutlined style={{ marginRight: 8, color: '#25D366' }} />
                    Kết nối WhatsApp
                </span>
            }
            style={{ maxWidth: 520, margin: '0 auto' }}
        >
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                <Step title="Hướng dẫn" icon={<LinkOutlined />} />
                <Step title={authMode === 'pairing_code' ? 'Liên kết' : 'Quét mã'} icon={<QrcodeOutlined />} />
                <Step title="Hoàn thành" icon={step === 2 ? <CheckCircleOutlined /> : step === 3 ? <CloseCircleOutlined /> : null} />
            </Steps>

            {loading && step === 0 ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16 }}>Đang khởi tạo session...</p>
                </div>
            ) : (
                renderStepContent()
            )}
        </Card>
    );
};

export default WhatsAppConnect;
